import { getUserProfile } from 'helpers/userProfile';
import { t } from 'i18next';
import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { colors } from '~/styles/colors';
import { useFilterContext } from '../contexts/FilterContext';
import { useThermalComfortDataContext } from '../contexts/ThermalComfortDataContext';
import { handleFilterStateRoute } from '../helpers/filters';
import { normalizeNumber } from '../helpers/numbers';
import { canViewThermalComfort } from '../helpers/thermalComfort';
import { BigNumberCard } from './components/Card';
import { GenericDescriptionTooltipContent } from './styles';

interface ThermalComfortProps {
  hasThermalComfort: boolean;
  loading: boolean;
}

export function ThermalComfort({ hasThermalComfort, loading }: Readonly<ThermalComfortProps>): ReactElement {
  const history = useHistory();
  const {
    loading: loadingThermalComfort, units, refetch,
  } = useThermalComfortDataContext();
  const [thermalComfortInfo, setThermalComfortInfo] = useState<{ averageIndex: number, acceptableUnits: number } | undefined>();
  const { selectedOptions, clients, filtersDependencies } = useFilterContext();
  const [profile] = useState(getUserProfile());

  const generalLoading = useMemo(
    () => loading || (loadingThermalComfort && hasThermalComfort && canViewThermalComfort(profile)),
    [hasThermalComfort, loading, loadingThermalComfort],
  );

  function parseThermalComfortInfo(unitsList: typeof units) {
    const nonInoperative = unitsList.filter((u) => u.thermalComfort !== 'inoperative');

    if (nonInoperative.length === 0) {
      setThermalComfortInfo({ averageIndex: 0, acceptableUnits: 0 });
      return;
    }

    const averageIndex = nonInoperative.reduce((sum, u) => sum + u.unitIndex, 0) / nonInoperative.length;
    const acceptableUnits = unitsList.filter(
      (u) => u.thermalComfort === 'comfortable' || u.thermalComfort === 'regular',
    ).length;

    setThermalComfortInfo({ averageIndex, acceptableUnits });
  }

  function indicatorColor(): string {
    if (!thermalComfortInfo) return colors.icon['disabled-300'];

    if (thermalComfortInfo.averageIndex > 7.0) return colors.state.success.base;
    if (thermalComfortInfo.averageIndex >= 5.0) return colors.state.warning.base;
    return colors.state.danger.base;
  }

  const comfortableUnitsCount = useMemo(
    () => units.filter((unit) => unit.thermalComfort === 'comfortable' || unit.thermalComfort === 'regular').length,
    [units],
  );

  const canSeeDetails = comfortableUnitsCount > 0;

  function onClickDetails(): void {
    history.push('/analise/conforto-termico');
  }

  useEffect(() => {
    if (hasThermalComfort && canViewThermalComfort(profile) && clients && clients.length > 0) {
      const clientIds = clients.map(Number);
      refetch(clientIds, selectedOptions);
    }
  }, [filtersDependencies]);

  useEffect(() => {
    if (canViewThermalComfort(profile)) {
      parseThermalComfortInfo(units);
    }
  }, [units]);

  return (
    <BigNumberCard
      id="thermal-comfort"
      icon="Thermometer2Icon"
      number={(thermalComfortInfo && thermalComfortInfo.averageIndex.toFixed(0)) || '0'}
      subnumber={!selectedOptions.status ? '/10' : undefined}
      description={{
        text: t('confortoTermicoMedido'),
        tooltip: <ThermalComfortTooltip />,
      }}
      subDescription={{
        number: normalizeNumber(thermalComfortInfo?.acceptableUnits),
        text: t('unidadesDentroDaFaixaIdealMedida'),
      }}
      loading={generalLoading}
      disabledTooltip={false}
      indicatorColor={indicatorColor()}
      needUpgrade={hasThermalComfort ? undefined : 'thermalComfort'}
      onClickDetails={hasThermalComfort && canSeeDetails ? onClickDetails : undefined}
      detailsDisabledReasonKey={hasThermalComfort && !canSeeDetails ? 'semUnidadesParaVerDetalhes' : undefined}
      dataTestId="big-number-card-thermal-comfort"
      comingSoon={!canViewThermalComfort(profile)}
      beta
    />
  );
}

function ThermalComfortTooltip() {
  return (
    <GenericDescriptionTooltipContent style={{ height: 80, width: 240 }}>
      <span>{t('numeroIndicaIndiceConfortoTermico')}</span>
    </GenericDescriptionTooltipContent>
  );
}
